import {
  LEGACY_CAPABILITY_ID,
  type LegacyMachinePark,
  type LegacyMachineParkWriteEnvelope,
  type MachinePark,
} from '@indutrust/factory-model';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * True when the payload already looks like a domain {@link MachinePark}.
 */
export function isDomainMachinePark(wire: unknown): wire is MachinePark {
  return (
    isRecord(wire) &&
    typeof wire['name'] === 'string' &&
    typeof wire['brand'] === 'string' &&
    Array.isArray(wire['capability_ids']) &&
    !('machine_year' in wire) &&
    !('operational_specs' in wire)
  );
}

/**
 * Maps a legacy machine-park payload to the domain {@link MachinePark}.
 *
 * Legacy fields `machine_year`, `production_capacity_available`, and
 * `operational_specs` have no domain counterpart yet and are dropped.
 * `capability_ids` defaults to `[LEGACY_CAPABILITY_ID]` when absent.
 */
export function legacyToMachinePark(wire: unknown): MachinePark {
  if (isDomainMachinePark(wire)) {
    return wire;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- legacy wire
  const legacy = wire as LegacyMachinePark;
  return {
    id: legacy.id,
    name: legacy.name,
    brand: legacy.brand ?? '',
    quantity: legacy.quantity ?? 1,
    capability_ids:
      legacy.capability_ids && legacy.capability_ids.length > 0
        ? legacy.capability_ids
        : [LEGACY_CAPABILITY_ID],
  };
}

/**
 * Builds the legacy `{ machine_park: … }` write envelope.
 */
export function machineParkToLegacy(
  entity: Partial<MachinePark>
): LegacyMachineParkWriteEnvelope {
  const machinePark: Record<string, unknown> = {};
  if (entity.name !== undefined) {
    machinePark['name'] = entity.name;
  }
  if (entity.brand !== undefined) {
    machinePark['brand'] = entity.brand;
  }
  if (entity.quantity !== undefined) {
    machinePark['quantity'] = entity.quantity;
  }
  return { machine_park: machinePark };
}
